/** Protocol error codes and the error type thrown by services and by the client. */

export const ERROR_CODES = [
  'BAD_REQUEST',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'VERSION_MISMATCH',
  'RATE_LIMITED',
  'BANNED',
  'INVALID_STATE',
  'VALIDATION_FAILED',
  'UNAVAILABLE',
  'INTERNAL',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export interface ProtocolError {
  code: ErrorCode;
  message: string;
  details?: unknown;
}

export function isErrorCode(v: unknown): v is ErrorCode {
  return typeof v === 'string' && (ERROR_CODES as readonly string[]).includes(v);
}

/** Error carrying a protocol error code. Services throw it; BackendClient rethrows it from error responses. */
export class BackendError extends Error {
  readonly code: ErrorCode;
  readonly details?: unknown;

  constructor(code: ErrorCode, message: string, details?: unknown) {
    super(message);
    this.name = 'BackendError';
    this.code = code;
    if (details !== undefined) this.details = details;
  }

  toProtocol(): ProtocolError {
    const e: ProtocolError = { code: this.code, message: this.message };
    if (this.details !== undefined) e.details = this.details;
    return e;
  }

  static fromProtocol(e: ProtocolError): BackendError {
    return new BackendError(e.code, e.message, e.details);
  }
}

export function isBackendError(e: unknown, code?: ErrorCode): e is BackendError {
  return e instanceof BackendError && (code === undefined || e.code === code);
}

/** Error codes a client may retry automatically with the same request id. */
export const RETRYABLE_CODES: ReadonlySet<ErrorCode> = new Set(['UNAVAILABLE', 'RATE_LIMITED']);

/** What each error code means for a client (published in the API description, `system.describe`). */
export const ERROR_CODE_INFO: { readonly [K in ErrorCode]: { description: string; retryable: boolean } } = {
  BAD_REQUEST: { description: 'Malformed envelope or payload, unknown request type, bad cursor, or a request id reused for another payload', retryable: false },
  UNAUTHORIZED: { description: 'No valid session: authenticate (or refresh the session) and retry', retryable: false },
  FORBIDDEN: { description: 'Not allowed for this player or disabled on this server (details.reason)', retryable: false },
  NOT_FOUND: { description: 'The addressed object does not exist', retryable: false },
  CONFLICT: { description: 'The object changed concurrently (details carry the current revision)', retryable: false },
  VERSION_MISMATCH: { description: 'No common protocol version (details.supported) or the client is too old (details.reason client_too_old)', retryable: false },
  RATE_LIMITED: { description: 'Too many requests: retry after details.retryAfterMs with the same request id', retryable: true },
  BANNED: { description: 'The account is banned (details: code, until)', retryable: false },
  INVALID_STATE: { description: 'The request does not fit the current state of the object (e.g. a room already started)', retryable: false },
  VALIDATION_FAILED: { description: 'A game rule refused the operation (details.reason is a stable machine code)', retryable: false },
  UNAVAILABLE: { description: 'The server is starting, stopping or overloaded: retry with the same request id', retryable: true },
  INTERNAL: { description: 'Unexpected server error (logged with the request and correlation ids)', retryable: false },
};

/**
 * Stable `details.reason` values the protocol layer itself produces. Every reason of every error (these, the
 * authentication, service and rule reasons) is listed in ./reasons.ts (ERROR_REASON_CATALOGUE, published by
 * system.describe).
 */
export const PROTOCOL_ERROR_REASONS = {
  /** BAD_REQUEST: a list cursor that this server did not issue for this list. */
  badCursor: 'bad_cursor',
  /** BAD_REQUEST: the request id was used before for a different payload. */
  idReused: 'id_reused',
  /** VERSION_MISMATCH: the client is older than the server's minimum client version (details.minClientVersion). */
  clientTooOld: 'client_too_old',
  /** VERSION_MISMATCH: none of the client's protocol versions is supported (details.supported). */
  unsupportedProtocol: 'unsupported_protocol',
  /** FORBIDDEN: the request belongs to a feature this server has disabled (details.feature). */
  featureDisabled: 'feature_disabled',
  /** BAD_REQUEST (HTTP 405): the HTTP method of an API path is wrong (details.allow). */
  methodNotAllowed: 'method_not_allowed',
  /** NOT_FOUND (HTTP 404): no API endpoint at this path under /api/. */
  unknownPath: 'unknown_path',
  /** BAD_REQUEST (HTTP 400): the HTTP body or the WebSocket message is not valid JSON. */
  invalidJson: 'invalid_json',
  /** BAD_REQUEST (HTTP 415): the Content-Type of an API request is not application/json. */
  unsupportedMediaType: 'unsupported_media_type',
  /** BAD_REQUEST (HTTP 413): the request is larger than limits.maxRequestBytes (details.maxBytes). */
  bodyTooLarge: 'body_too_large',
  /** BAD_REQUEST: not a request envelope `{ v, id, type, payload?, correlationId? }` (details.field: the bad field). */
  invalidEnvelope: 'invalid_envelope',
  /** BAD_REQUEST: no request type of that name (details.type). */
  unknownType: 'unknown_type',
  /** BAD_REQUEST: the payload does not match the schema of its request type (details.issues). */
  invalidPayload: 'invalid_payload',
} as const;
